import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  BarChart, 
  Copy, 
  Download, 
  Trash2, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  RefreshCw,
  Package,
  Zap,
  Eye,
  EyeOff,
  AlertCircle,
  Hash,
  CreditCard,
  ShoppingCart,
  Truck,
  Archive,
  Tag
} from 'lucide-react';

interface BarcodeSettings {
  barcodeType: 'code128' | 'code39' | 'ean13' | 'ean8' | 'upc_a' | 'upc_e' | 'itf' | 'msi' | 'pharmacode' | 'codabar';
  width: number;
  height: number;
  includeText: boolean;
  fontSize: number;
  margin: number;
  backgroundColor: string;
  foregroundColor: string;
  format: 'svg' | 'png' | 'jpeg';
  // Advanced options
  quietZone: boolean;
  checksum: boolean;
  prefix: string;
  suffix: string;
  textPosition: 'top' | 'bottom' | 'none';
  fontFamily: 'Arial' | 'Helvetica' | 'Courier' | 'Times';
  lineHeight: number;
  displayValue: boolean;
}

interface GeneratedBarcode {
  id: string;
  barcodeType: string;
  content: string;
  displayContent: string;
  barcodeData: string;
  width: number;
  height: number;
  backgroundColor: string;
  foregroundColor: string;
  timestamp: Date;
  format: string;
}

interface Analytics {
  totalGenerated: number;
  typeDistribution: Record<string, number>;
  sizeDistribution: Record<string, number>;
  formatDistribution: Record<string, number>;
  colorUsage: Record<string, number>;
  contentLengthDistribution: Record<number, number>;
  sessionTime: number;
  averageWidth: number;
  averageHeight: number;
  lastGenerated: Date | null;
}

type QualityLevel = 'standard' | 'high' | 'premium';

const BarcodeGenerator: React.FC = () => {
  const [content, setContent] = useState<string>('1234567890');
  const [barcode, setBarcode] = useState<GeneratedBarcode | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [qualityLevel, setQualityLevel] = useState<QualityLevel>('high');
  const [showBarcode, setShowBarcode] = useState<boolean>(true);

  const [settings, setSettings] = useState<BarcodeSettings>({
    barcodeType: 'code128',
    width: 200,
    height: 80,
    includeText: true,
    fontSize: 12,
    margin: 10,
    backgroundColor: '#FFFFFF',
    foregroundColor: '#000000',
    format: 'svg',
    quietZone: true,
    checksum: true,
    prefix: '',
    suffix: '',
    textPosition: 'bottom',
    fontFamily: 'Arial',
    lineHeight: 1.2,
    displayValue: true
  });

  const [analytics, setAnalytics] = useState<Analytics>({
    totalGenerated: 0,
    typeDistribution: {},
    sizeDistribution: {},
    formatDistribution: {},
    colorUsage: {},
    contentLengthDistribution: {},
    sessionTime: 0,
    averageWidth: 0,
    averageHeight: 0,
    lastGenerated: null
  });

  // SEO Meta Data
  const seoData = useMemo(() => {
    const baseTitle = "Barcode Generator";
    const dynamicTitle = barcode 
      ? `${baseTitle} - Generated ${settings.barcodeType.toUpperCase()} Barcode`
      : `${baseTitle} - Create Professional Barcodes Online`;
    
    const description = barcode
      ? `Generated ${settings.barcodeType.toUpperCase()} barcode for "${content}". Professional barcode generator with advanced customization options. Download as ${settings.format.toUpperCase()} or copy to clipboard.`
      : "Generate professional barcodes for products, inventory, and shipping. Support for Code 128, Code 39, EAN-13, UPC-A, and more. Advanced customization and analytics. Free barcode generator.";

    const keywords = [
      "barcode generator",
      "code 128 generator",
      "ean 13 barcode",
      "upc barcode generator",
      "product barcode maker",
      "inventory barcode",
      "shipping barcode",
      "custom barcode",
      "barcode creator",
      "business barcode tool",
      "retail barcode",
      "warehouse barcode",
      "professional barcode",
      "barcode analytics",
      "barcode customization"
    ].join(", ");

    return { title: dynamicTitle, description, keywords };
  }, [content, barcode, settings.barcodeType, settings.format]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Barcode Generator",
    "description": seoData.description,
    "url": "https://example.com/barcode-generator",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "All",
    "permissions": "none required",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Code 128 barcodes",
      "EAN-13 and UPC-A barcodes",
      "Custom colors and sizes",
      "Multiple export formats",
      "Barcode analytics",
      "Advanced customization",
      "Professional quality",
      "Instant generation"
    ],
    "browserRequirements": "Requires JavaScript enabled",
    "publisher": {
      "@type": "Organization",
      "name": "Barcode Tools"
    },
    "softwareVersion": "1.0",
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString(),
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "usageInfo": "Free to use for personal and commercial purposes"
  };

  // Barcode type configurations
  const barcodeTypeConfig = {
    code128: { icon: BarChart, label: 'Code 128', description: 'High-density, alphanumeric', maxLength: 80, placeholder: '1234567890ABC' },
    code39: { icon: Hash, label: 'Code 39', description: 'Alphanumeric, widely supported', maxLength: 43, placeholder: 'HELLO123' },
    ean13: { icon: CreditCard, label: 'EAN-13', description: 'Product identification (13 digits)', maxLength: 13, placeholder: '1234567890123' },
    ean8: { icon: Tag, label: 'EAN-8', description: 'Compact product codes (8 digits)', maxLength: 8, placeholder: '12345678' },
    upc_a: { icon: ShoppingCart, label: 'UPC-A', description: 'North American products (12 digits)', maxLength: 12, placeholder: '123456789012' },
    upc_e: { icon: Package, label: 'UPC-E', description: 'Compact UPC (8 digits)', maxLength: 8, placeholder: '01234567' },
    itf: { icon: Truck, label: 'ITF-14', description: 'Shipping containers (14 digits)', maxLength: 14, placeholder: '12345678901234' },
    msi: { icon: Archive, label: 'MSI', description: 'Inventory management', maxLength: 55, placeholder: '1234567890' },
    pharmacode: { icon: Package, label: 'Pharmacode', description: 'Pharmaceutical packaging', maxLength: 6, placeholder: '123456' },
    codabar: { icon: BarChart, label: 'Codabar', description: 'Libraries, blood banks', maxLength: 16, placeholder: 'A123456789B' }
  };

  const generateBarcodeData = useCallback((inputContent: string, type: string): string => {
    // This is a placeholder - in production, use libraries like 'jsbarcode' or 'bwip-js'
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = settings.width;
    canvas.height = settings.height;
    
    if (ctx) {
      // Background
      ctx.fillStyle = settings.backgroundColor;
      ctx.fillRect(0, 0, settings.width, settings.height);
      
      // Generate barcode pattern based on content and type
      const hash = inputContent.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      
      const margin = settings.quietZone ? settings.margin : 0;
      const barcodeWidth = settings.width - (margin * 2);
      const barcodeHeight = settings.includeText ? settings.height - settings.fontSize - 10 : settings.height - (margin * 2);
      const startX = margin;
      const startY = margin;
      
      ctx.fillStyle = settings.foregroundColor;
      
      // Generate pattern based on barcode type
      const barCount = type === 'ean13' || type === 'upc_a' ? 95 : 
                      type === 'ean8' || type === 'upc_e' ? 67 :
                      Math.floor(barcodeWidth / 2);
      
      const barWidth = barcodeWidth / barCount;
      
      // Create deterministic pattern based on content
      for (let i = 0; i < barCount; i++) {
        const seedValue = (hash + i * 7 + inputContent.charCodeAt(i % inputContent.length)) % 1000;
        const isBar = seedValue % 2 === 0;
        
        if (isBar) {
          const x = startX + (i * barWidth);
          ctx.fillRect(x, startY, Math.max(1, barWidth * 0.8), barcodeHeight);
        }
      }
      
      // Add start/end patterns for specific types
      if (type === 'ean13' || type === 'upc_a' || type === 'ean8') {
        // Start pattern
        ctx.fillRect(startX, startY, barWidth, barcodeHeight);
        ctx.fillRect(startX + barWidth * 2, startY, barWidth, barcodeHeight);
        
        // End pattern
        ctx.fillRect(startX + barcodeWidth - barWidth * 3, startY, barWidth, barcodeHeight);
        ctx.fillRect(startX + barcodeWidth - barWidth, startY, barWidth, barcodeHeight);
        
        // Center pattern for EAN/UPC
        const centerX = startX + barcodeWidth / 2;
        ctx.fillRect(centerX - barWidth, startY, barWidth, barcodeHeight);
        ctx.fillRect(centerX + barWidth, startY, barWidth, barcodeHeight);
      }
      
      // Add text if enabled
      if (settings.includeText && settings.displayValue) {
        ctx.fillStyle = settings.foregroundColor;
        ctx.font = `${settings.fontSize}px ${settings.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        
        const textY = settings.textPosition === 'top' ? 5 : 
                     settings.height - settings.fontSize - 5;
        
        const displayText = `${settings.prefix}${inputContent}${settings.suffix}`;
        ctx.fillText(displayText, settings.width / 2, textY);
      }
    }
    
    return canvas.toDataURL(`image/${settings.format === 'svg' ? 'png' : settings.format}`);
  }, [settings]);

  const validateContent = useCallback((inputContent: string, type: string): boolean => {
    const config = barcodeTypeConfig[type as keyof typeof barcodeTypeConfig];
    if (inputContent.length > config.maxLength) return false;
    
    switch (type) {
      case 'ean13':
        return /^\d{12,13}$/.test(inputContent);
      case 'ean8':
        return /^\d{7,8}$/.test(inputContent);
      case 'upc_a':
        return /^\d{11,12}$/.test(inputContent);
      case 'upc_e':
        return /^\d{6,8}$/.test(inputContent);
      case 'itf':
        return /^\d+$/.test(inputContent) && inputContent.length % 2 === 0;
      case 'code39':
        return /^[A-Z0-9\-\.\$\/\+\%\*\s]+$/.test(inputContent);
      case 'pharmacode':
        return /^\d{1,6}$/.test(inputContent);
      default:
        return inputContent.length > 0;
    }
  }, []);

  const generateBarcode = useCallback(async () => {
    if (!content.trim()) return;
    
    setIsGenerating(true);
    
    // Simulate processing time based on quality level
    const delay = qualityLevel === 'premium' ? 800 : qualityLevel === 'high' ? 500 : 200;
    await new Promise(resolve => setTimeout(resolve, delay));
    
    let validContent = content.trim();
    
    // Auto-correct content for specific barcode types if invalid
    if (!validateContent(validContent, settings.barcodeType)) {
      switch (settings.barcodeType) {
        case 'ean13':
          validContent = '1234567890123';
          break;
        case 'upc_a':
          validContent = '123456789012';
          break;
        case 'ean8':
          validContent = '12345678';
          break;
        case 'upc_e':
          validContent = '01234567';
          break;
        case 'itf':
          validContent = '1234567890123456';
          break;
        case 'pharmacode':
          validContent = '123456';
          break;
        default:
          validContent = validContent || '1234567890';
      }
    }
    
    const barcodeData = generateBarcodeData(validContent, settings.barcodeType);
    const displayContent = `${settings.prefix}${validContent}${settings.suffix}`;

    const newBarcode: GeneratedBarcode = {
      id: Math.random().toString(36).substr(2, 9),
      barcodeType: settings.barcodeType,
      content: validContent,
      displayContent,
      barcodeData,
      width: settings.width,
      height: settings.height,
      backgroundColor: settings.backgroundColor,
      foregroundColor: settings.foregroundColor,
      timestamp: new Date(),
      format: settings.format
    };
    
    setBarcode(newBarcode);
    
    // Update analytics
    setAnalytics(prev => {
      const total = prev.totalGenerated + 1;
      const avgWidth = (prev.averageWidth * prev.totalGenerated + newBarcode.width) / total;
      const avgHeight = (prev.averageHeight * prev.totalGenerated + newBarcode.height) / total;
      
      const typeDist = { ...prev.typeDistribution };
      const sizeDist = { ...prev.sizeDistribution };
      const formatDist = { ...prev.formatDistribution };
      const colorUsage = { ...prev.colorUsage };
      const lengthDist = { ...prev.contentLengthDistribution };
      
      typeDist[newBarcode.barcodeType] = (typeDist[newBarcode.barcodeType] || 0) + 1;
      const sizeKey = `${newBarcode.width}x${newBarcode.height}`;
      sizeDist[sizeKey] = (sizeDist[sizeKey] || 0) + 1;
      formatDist[newBarcode.format] = (formatDist[newBarcode.format] || 0) + 1;
      colorUsage[newBarcode.foregroundColor] = (colorUsage[newBarcode.foregroundColor] || 0) + 1;
      lengthDist[newBarcode.content.length] = (lengthDist[newBarcode.content.length] || 0) + 1;

      return {
        totalGenerated: total,
        typeDistribution: typeDist,
        sizeDistribution: sizeDist,
        formatDistribution: formatDist,
        colorUsage,
        contentLengthDistribution: lengthDist,
        sessionTime: prev.sessionTime,
        averageWidth: Math.round(avgWidth),
        averageHeight: Math.round(avgHeight),
        lastGenerated: new Date()
      };
    });

    setIsGenerating(false);
  }, [content, settings, qualityLevel, validateContent, generateBarcodeData]);

  const copyBarcode = async () => {
    if (!barcode) return;
    
    try {
      const response = await fetch(barcode.barcodeData);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback to copying content
      try {
        await navigator.clipboard.writeText(barcode.content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Failed to copy barcode');
      }
    }
  };

  const downloadBarcode = () => {
    if (!barcode) return;
    
    const link = document.createElement('a');
    link.download = `barcode-${barcode.barcodeType}-${Date.now()}.${barcode.format}`;
    link.href = barcode.barcodeData;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearBarcode = () => {
    setBarcode(null);
  };

  const getBarcodeTypeColor = (barcodeType: string): string => {
    const colors = {
      code128: 'text-blue-500',
      code39: 'text-green-500',
      ean13: 'text-purple-500',
      ean8: 'text-orange-500',
      upc_a: 'text-red-500',
      upc_e: 'text-pink-500',
      itf: 'text-indigo-500',
      msi: 'text-yellow-500',
      pharmacode: 'text-teal-500',
      codabar: 'text-gray-500'
    };
    return colors[barcodeType as keyof typeof colors] || 'text-gray-500';
  };

  const getQualityLevelColor = (level: QualityLevel): string => {
    switch (level) {
      case 'standard': return 'bg-yellow-500';
      case 'high': return 'bg-blue-500';
      case 'premium': return 'bg-green-500';
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isGenerating) {
      generateBarcode();
    }
  };

  // Auto-generate on first load
  useEffect(() => {
    generateBarcode();
  }, []);

  // Session time tracking
  useEffect(() => {
    const interval = setInterval(() => {
      setAnalytics(prev => ({ ...prev, sessionTime: prev.sessionTime + 1 }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-purple-100'}
    transition-all duration-300
  `;

  return (
    <div className={containerClasses}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="Barcode Tools" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://example.com/barcode-generator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://example.com/barcode-generator" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://example.com/barcode-generator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Professional Barcode Generator Tool" />
        <meta property="og:site_name" content="Barcode Tools" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://example.com/barcode-generator" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content="https://example.com/barcode-generator-preview.jpg" />
        <meta property="twitter:image:alt" content="Simple Barcode Generator - Professional Results" />
        <meta name="twitter:creator" content="@barcodetools" />
        <meta name="twitter:site" content="@barcodetools" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={isDarkMode ? "#1f2937" : "#3b82f6"} />
        <meta name="msapplication-TileColor" content="#3b82f6" />
        <meta name="application-name" content="Barcode Generator" />
        <meta name="apple-mobile-web-app-title" content="Barcode Generator" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Performance and Security */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="referrer" content="origin-when-cross-origin" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How do I generate a barcode?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Enter your barcode data in the input field and click Generate. The barcode will be created instantly with your chosen settings. You can then download or copy the barcode."
                }
              },
              {
                "@type": "Question", 
                "name": "What barcode types are supported?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Support for Code 128, Code 39, EAN-13, EAN-8, UPC-A, UPC-E, ITF-14, MSI, Pharmacode, and Codabar. Each type has specific formatting requirements and use cases."
                }
              },
              {
                "@type": "Question",
                "name": "Can I customize the barcode appearance?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! Customize width, height, colors, fonts, add text, adjust margins, and choose export formats. Access advanced options in the settings panel."
                }
              },
              {
                "@type": "Question",
                "name": "Is this barcode generator free?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, completely free to use for personal and commercial purposes. Generate professional-quality barcodes with advanced customization options."
                }
              }
            ]
          })}
        </script>
        
        {/* Dynamic Schema for Generated Barcode */}
        {barcode && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "DigitalDocument",
              "name": `${barcode.barcodeType.toUpperCase()} Barcode for ${barcode.content}`,
              "description": `Generated ${barcode.barcodeType.toUpperCase()} barcode containing: ${barcode.content}`,
              "encodingFormat": `image/${barcode.format}`,
              "dateCreated": barcode.timestamp.toISOString(),
              "creator": {
                "@type": "SoftwareApplication",
                "name": "Barcode Generator"
              }
            })}
          </script>
        )}
      </Helmet>
      
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          {/* Header */}
          <div className="max-w-7xl mx-auto mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 gap-4">
              <div className="flex items-center space-x-3">
                <BarChart className="w-8 h-8 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    Barcode Generator
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Generate professional barcodes with advanced customization
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap">
                {/* Quality Level */}
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Quality:</span>
                  <select
                    value={qualityLevel}
                    onChange={(e) => setQualityLevel(e.target.value as QualityLevel)}
                    className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                    aria-label="Select quality level for barcode generation"
                  >
                    <option value="standard">Standard</option>
                    <option value="high">High</option>
                    <option value="premium">Premium</option>
                  </select>
                  <div className={`w-2 h-2 rounded-full ${getQualityLevelColor(qualityLevel)}`} />
                </div>

                {/* Action Buttons */}
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Analytics"
                >
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Settings"
                >
                  <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
                
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className={`${showSettings || showAnalytics ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
              <div className="max-w-3xl mx-auto">
                {/* Input Section */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <label htmlFor="barcode-input" className="block text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Enter barcode data:
                        </label>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {barcodeTypeConfig[settings.barcodeType].description} • Max {barcodeTypeConfig[settings.barcodeType].maxLength} characters
                        </p>
                      </div>
                      <select
                        value={settings.barcodeType}
                        onChange={(e) => {
                          setSettings(prev => ({ ...prev, barcodeType: e.target.value as any }));
                          setContent(barcodeTypeConfig[e.target.value as keyof typeof barcodeTypeConfig].placeholder);
                        }}
                        className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300"
                      >
                        {Object.entries(barcodeTypeConfig).map(([key, config]) => (
                          <option key={key} value={key}>{config.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <input
                      id="barcode-input"
                      type="text"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={barcodeTypeConfig[settings.barcodeType].placeholder}
                      className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors font-mono"
                      disabled={isGenerating}
                    />
                    
                    <button
                      onClick={generateBarcode}
                      disabled={isGenerating || !content.trim()}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 text-lg"
                    >
                      {isGenerating ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <Zap className="w-5 h-5" />
                      )}
                      <span>{isGenerating ? 'Generating...' : 'Generate Barcode'}</span>
                    </button>
                  </div>
                </div>

                {/* Barcode Display */}
                {barcode && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Your {barcodeTypeConfig[barcode.barcodeType as keyof typeof barcodeTypeConfig]?.label} Barcode
                      </h3>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setShowBarcode(!showBarcode)}
                          className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          title={showBarcode ? "Hide Barcode" : "Show Barcode"}
                        >
                          {showBarcode ? (
                            <EyeOff className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                          )}
                        </button>
                        <button
                          onClick={clearBarcode}
                          className="p-2 rounded bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                          title="Clear Barcode"
                        >
                          <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                        </button>
                      </div>
                    </div>
                    
                    {showBarcode && (
                      <div className="inline-block p-6 bg-white rounded-lg shadow-inner mb-6">
                        <img
                          src={barcode.barcodeData}
                          alt={`${barcode.barcodeType.toUpperCase()} Barcode for ${barcode.displayContent}`}
                          className="mx-auto"
                          style={{ maxWidth: '400px', height: 'auto' }}
                        />
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-sm">
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-gray-600 dark:text-gray-400">Type</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {barcodeTypeConfig[barcode.barcodeType as keyof typeof barcodeTypeConfig]?.label}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-gray-600 dark:text-gray-400">Content</p>
                        <p className="font-mono font-medium text-gray-900 dark:text-white break-all">
                          {barcode.displayContent}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                        <p className="text-gray-600 dark:text-gray-400">Dimensions</p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {barcode.width} × {barcode.height}px
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <button
                        onClick={copyBarcode}
                        className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors font-medium"
                      >
                        {copied ? (
                          <Check className="w-5 h-5 text-green-500" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                        <span>{copied ? 'Copied!' : 'Copy to Clipboard'}</span>
                      </button>
                      
                      <button
                        onClick={downloadBarcode}
                        className="flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                      >
                        <Download className="w-5 h-5" />
                        <span>Download {settings.format.toUpperCase()}</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Help Text */}
                {!barcode && (
                  <div className="text-center">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                      <Package className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-2">
                        Professional Barcode Generation
                      </h3>
                      <p className="text-blue-700 dark:text-blue-300">
                        Enter your product code, inventory number, or any data to generate a professional barcode. 
                        Customize appearance and export options in the settings panel.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4 max-h-screen overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Barcode Settings
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Dimensions */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Dimensions</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Width: {settings.width}px
                          </label>
                          <input
                            type="range"
                            min="100"
                            max="400"
                            step="10"
                            value={settings.width}
                            onChange={(e) => setSettings(prev => ({ ...prev, width: parseInt(e.target.value) }))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Height: {settings.height}px
                          </label>
                          <input
                            type="range"
                            min="40"
                            max="200"
                            step="5"
                            value={settings.height}
                            onChange={(e) => setSettings(prev => ({ ...prev, height: parseInt(e.target.value) }))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Appearance */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Appearance</h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Foreground</label>
                            <input
                              type="color"
                              value={settings.foregroundColor}
                              onChange={(e) => setSettings(prev => ({ ...prev, foregroundColor: e.target.value }))}
                              className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Background</label>
                            <input
                              type="color"
                              value={settings.backgroundColor}
                              onChange={(e) => setSettings(prev => ({ ...prev, backgroundColor: e.target.value }))}
                              className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Font Size: {settings.fontSize}px</label>
                          <input
                            type="range"
                            min="8"
                            max="24"
                            value={settings.fontSize}
                            onChange={(e) => setSettings(prev => ({ ...prev, fontSize: parseInt(e.target.value) }))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Font Family</label>
                          <select
                            value={settings.fontFamily}
                            onChange={(e) => setSettings(prev => ({ ...prev, fontFamily: e.target.value as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Arial">Arial</option>
                            <option value="Helvetica">Helvetica</option>
                            <option value="Courier">Courier</option>
                            <option value="Times">Times</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Export Format</label>
                          <select
                            value={settings.format}
                            onChange={(e) => setSettings(prev => ({ ...prev, format: e.target.value as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="svg">SVG (Vector)</option>
                            <option value="png">PNG (Raster)</option>
                            <option value="jpeg">JPEG (Compressed)</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Text Options */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Text Options</h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Prefix</label>
                            <input
                              type="text"
                              value={settings.prefix}
                              onChange={(e) => setSettings(prev => ({ ...prev, prefix: e.target.value }))}
                              placeholder="Prefix"
                              className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Suffix</label>
                            <input
                              type="text"
                              value={settings.suffix}
                              onChange={(e) => setSettings(prev => ({ ...prev, suffix: e.target.value }))}
                              placeholder="Suffix"
                              className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Text Position</label>
                          <select
                            value={settings.textPosition}
                            onChange={(e) => setSettings(prev => ({ ...prev, textPosition: e.target.value as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="bottom">Bottom</option>
                            <option value="top">Top</option>
                            <option value="none">None</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Advanced Options */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Advanced Options</h4>
                      {[
                        { key: 'includeText', label: 'Include Text', desc: 'Show barcode value as text' },
                        { key: 'quietZone', label: 'Quiet Zone', desc: 'Add margins around barcode' },
                        { key: 'checksum', label: 'Checksum', desc: 'Include error detection digit' },
                        { key: 'displayValue', label: 'Display Value', desc: 'Show human-readable text' }
                      ].map(({ key, label, desc }) => (
                        <div key={key}>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof BarcodeSettings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Analytics
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {analytics.totalGenerated}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Total Generated
                        </div>
                      </div>
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                          {Object.keys(analytics.typeDistribution).length}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Types Used
                        </div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {analytics.averageWidth}×{analytics.averageHeight}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Size (px)
                        </div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                          {Math.floor(analytics.sessionTime / 60)}m
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Session Time
                        </div>
                      </div>
                    </div>

                    {/* Barcode Type Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Barcode Types
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.typeDistribution).map(([type, count]) => (
                          <div key={type} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400">
                              {barcodeTypeConfig[type as keyof typeof barcodeTypeConfig]?.label || type}
                            </span>
                            <span className="font-medium text-gray-800 dark:text-white">
                              {count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Popular Sizes */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Popular Sizes
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {Object.entries(analytics.sizeDistribution)
                          .sort(([,a], [,b]) => b - a)
                          .slice(0, 5)
                          .map(([size, count]) => (
                          <div key={size} className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                            <span>{size}px</span>
                            <span>{count} times</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Content Length Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Content Lengths
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {Object.entries(analytics.contentLengthDistribution)
                          .sort(([,a], [,b]) => b - a)
                          .slice(0, 5)
                          .map(([length, count]) => (
                          <div key={length} className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                            <span>{length} chars</span>
                            <span>{count} times</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Quality Info */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Quality Level
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${getQualityLevelColor(qualityLevel)}`} />
                        <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {qualityLevel}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {qualityLevel === 'premium' 
                          ? 'Maximum resolution and precision' 
                          : qualityLevel === 'high'
                          ? 'Balanced quality and performance'
                          : 'Fast generation, standard quality'}
                      </p>
                    </div>

                    {/* Last Generated */}
                    {analytics.lastGenerated && (
                      <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                        Last generated: {analytics.lastGenerated.toLocaleTimeString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeGenerator;